import { Box, Card, Typography, Divider } from '@mui/material';

const SectionItemFormGuidelines = () => {
    return (
        <Card sx={{ p: { xs: 2, md: 4 } , maxHeight:'74vh', overflowY: 'auto' }}>
            {/* <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>Section Item Form Guidelines</Typography> */}
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Section Assignment</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Section *</Typography>
                        <Typography variant="body2" color="text.secondary">
                            The parent section this item belongs to. Use the autocomplete field to search and select from available sections.
                            Each item must be assigned to a section which controls how and where it will be displayed in the app.
                            This field is required before saving the item.
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Basic Information</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Title *</Typography>
                        <Typography variant="body2" color="text.secondary">
                            The main heading/title for this item. This will be displayed prominently on the item card in the app.
                            Use a clear, concise title that describes the item content or purpose.
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'warning.main', mt: 0.5 }}>Max 30 characters (including spaces).</Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Subtitle *</Typography>
                        <Typography variant="body2" color="text.secondary">
                            A secondary line of text that appears below the title. Use this to provide additional context or brief details
                            about the item. Keep it short and informative, such as price, category, or a brief tagline.
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'warning.main', mt: 0.5 }}>Max 40 characters (including spaces).</Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Description</Typography>
                        <Typography variant="body2" color="text.secondary">
                            A detailed description of the item. This field supports multi-line text and is used to provide comprehensive
                            information about the item. The description visibility can be controlled via Display Options.
                            When Display Options showDescription is enabled, this field becomes required.
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Display Options</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Collapsible section that controls which text elements are visible and their alignment positions.
                    </Typography>

                    <Box sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Show Title</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Toggle to show/hide the item title. When enabled, you can select title alignment (Left/Center/Right).
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Show Subtitle</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Toggle to show/hide the item subtitle. When enabled, you can select subtitle alignment (Left/Center/Right).
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Show Description</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Toggle to show/hide the item description. When enabled, you can select description alignment (Left/Center/Right).
                            Enabling this also makes the description field required.
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Image Management</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload and manage images for this item. Each image type serves a different display purpose.
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Image URL</Typography>
                        <Typography variant="body2" color="text.secondary">
                            The main image displayed for this item. Click "Upload" to select an image file.
                            This is typically the largest and highest quality image, shown when the item is featured or selected.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Thumbnail URL</Typography>
                        <Typography variant="body2" color="text.secondary">
                            A smaller version of the image for grid views and lists. Used when displaying multiple items
                            in a section to improve performance and maintain visual consistency 
                            <Box component={'span'} sx={{color:'#389009'}}>
                                (Currently not in use, reserved for future grid/list display modes).
                            </Box>.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Icon URL</Typography>
                        <Typography variant="body2" color="text.secondary">
                            A small icon or badge image associated with the item. Can be used for category indicators,
                            brand logos, or small visual markers on the item card
                            <Box component={'span'} sx={{color:'#389009'}}>
                                (Currently not in use, reserved for future icon display modes).
                            </Box>.
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Action Configuration</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Defines what happens when a user taps/clicks on this item. Choose the appropriate action type:
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Navigate</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Navigate to an internal app route/path. Enter the route as JSON: {'{"route": "/path"}'}.
                                Use the Generate App Url button to auto-generate routes.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Open Service</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Open a specific service by its ID. Enter the service ID number
                                <Box component={'span'} sx={{color:'#389009'}}>
                                    (Currently not in use, reserved for future service display modes).
                                </Box>.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Open Category</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Open a specific category by its ID. Enter the category ID number
                                <Box component={'span'} sx={{color:'#389009'}}>
                                    (Currently not in use, reserved for future category display modes).
                                </Box>.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Open URL</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Open an external URL. Enter the full URL including https://
                                <Box component={'span'} sx={{color:'#389009'}}>
                                    (Currently not in use, reserved for future URL display modes).
                                </Box>.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Show Popup</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Display a popup overlay. Requires both Popup ID and Popup Content
                                <Box component={'span'} sx={{color:'#389009'}}>
                                    (Currently not in use, reserved for future popup display modes).
                                </Box>.
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>None</Typography>
                            <Typography variant="body2" color="text.secondary">
                                No action on tap. Useful for informational items or disabled states.
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Badge Settings
                        <Box component={'span'} sx={{color:'#389009', fontSize:'0.875rem', ml: 1}}>
                            (Optional visual labels on item images)
                        </Box>
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Badge Text</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Optional text overlay on the item image. Common uses include "NEW", "SALE", "HOT", "LIMITED", or promotional labels.
                            Leave empty for no badge.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Badge Color</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Background color for the badge. Use the color picker or enter a hex code (e.g., #FF6B35).
                            The badge text will be displayed in white for contrast.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Badge Position</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Position of the badge on the item image:
                        </Typography>
                        <Box sx={{ mt: 1, pl: 2 }}>
                            <Typography variant="body2">• <strong>Top Left:</strong> Badge appears in upper-left corner</Typography>
                            <Typography variant="body2">• <strong>Top Right:</strong> Badge appears in upper-right corner</Typography>
                            <Typography variant="body2">• <strong>Bottom Left:</strong> Badge appears in lower-left corner</Typography>
                            <Typography variant="body2">• <strong>Bottom Right:</strong> Badge appears in lower-right corner</Typography>
                        </Box>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Card Styling</Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Card Background Color</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Background color behind the item card content. Use the color picker or enter a hex code (e.g., #FFFFFF).
                            This helps distinguish the card from the section background.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Card Border Color</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Border color around the item card. Use the color picker or enter a hex code (e.g., #E0E0E0).
                            Leave empty or use transparent for no visible border.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Aspect Ratio</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Controls the image dimensions relative to width:
                        </Typography>
                        <Box sx={{ mt: 1, pl: 2 }}>
                            <Typography variant="body2">• <strong>1:1:</strong> Square image (equal width and height)</Typography>
                            <Typography variant="body2">• <strong>4:3:</strong> Standard aspect ratio (4 units wide, 3 units tall)</Typography>
                            <Typography variant="body2">• <strong>16:9:</strong> Widescreen format (ideal for banners)</Typography>
                            <Typography variant="body2">• <strong>3:4:</strong> Portrait format (taller than wide)</Typography>
                            <Typography variant="body2">• <strong>9:16:</strong> Vertical format (mobile-style)</Typography>
                        </Box>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Scheduling</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Start Date/Time</Typography>
                        <Typography variant="body2" color="text.secondary">
                            The date and time when this item becomes visible in the app. Leave empty to show immediately.
                            Use this to schedule items for future promotions or campaigns.
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>End Date/Time</Typography>
                        <Typography variant="body2" color="text.secondary">
                            The date and time when this item expires and becomes hidden from the app. Leave empty for no expiration.
                            Use this for time-limited promotions, seasonal items, or flash sales.
                        </Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>Service Relations</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                        Below the main form, the ServiceRelationManager component allows you to link this item to specific services.
                        This enables cross-referencing between items and services in the app, useful for related content,
                        upselling, or service discovery features.
                    </Typography>
                </Box>

            </Box>
        </Card>
    );
};

export default SectionItemFormGuidelines;
